import { useLayoutEffect, useRef, useState } from 'react';
import { useUpdateCartTransport } from '~/api';
import { useLatestEdit } from '~/hooks/useLatestEdit';
import { deliveryMethodToIncoTerm } from '~/hooks/useTransportState';
import type { DomesticDeliveryMethod } from '~/types/general';
import { findGraphQLErrorWithCode, type ClientError } from '~/utils';
import type { TransportSaveFailure } from '~/types/transportSave';

export const VEHICLE_CAPACITY_MISSING_CODE = 'vehicle-capacity-missing';

export function isVehicleCapacityMissingError(error: ClientError): boolean {
  return (
    findGraphQLErrorWithCode(error, VEHICLE_CAPACITY_MISSING_CODE) !== undefined
  );
}

const NO_TRANSPORT_SAVE_FAILURE: TransportSaveFailure = { kind: 'none' };

function toTransportSaveFailure(error: ClientError): TransportSaveFailure {
  return isVehicleCapacityMissingError(error)
    ? { kind: 'missing-capacity' }
    : { kind: 'other' };
}

const sendKey = (method: DomesticDeliveryMethod, vehicleId: string) =>
  `${method}:${vehicleId}`;

/**
 * Saves a cart's delivery method and vehicle, and reports why the latest save failed. A response
 * to an earlier save is ignored once a later one has been sent, and a retry of the save already
 * in flight is not sent twice.
 */
export function useCartTransportSave(cartId: string) {
  const { mutate: updateCartTransport } = useUpdateCartTransport();
  const [failure, setFailure] = useState<TransportSaveFailure>(
    NO_TRANSPORT_SAVE_FAILURE
  );
  const [inFlightSend, setInFlightSend] = useState<string | null>(null);
  const { begin, isLatest } = useLatestEdit();

  const send = (method: DomesticDeliveryMethod, vehicleId: string) => {
    const edit = begin();
    setInFlightSend(sendKey(method, vehicleId));
    updateCartTransport(
      {
        cartId,
        incoTerm: deliveryMethodToIncoTerm(method),
        transportVehicleId: vehicleId,
      },
      {
        onError: (error) => {
          if (!isLatest(edit)) return;
          setInFlightSend(null);
          setFailure(toTransportSaveFailure(error));
        },
        onSuccess: () => {
          if (!isLatest(edit)) return;
          setInFlightSend(null);
          setFailure(NO_TRANSPORT_SAVE_FAILURE);
        },
      }
    );
  };

  const save = (method: DomesticDeliveryMethod, vehicleId: string) => {
    begin();
    setInFlightSend(null);
    setFailure(NO_TRANSPORT_SAVE_FAILURE);
    if (!vehicleId) return;
    send(method, vehicleId);
  };

  const retry = (method: DomesticDeliveryMethod, vehicleId: string) => {
    if (!vehicleId) return;
    if (inFlightSend === sendKey(method, vehicleId)) return;
    send(method, vehicleId);
  };

  return { failure, save, retry, isSaving: inFlightSend !== null };
}

export function useRetryTransportSaveWhen(
  shouldRetry: boolean,
  retry: () => void
) {
  const hasRetriedRef = useRef(false);

  // Before paint, so the banner never shows "wasn't saved" for the frame between the capacity
  // arriving and the re-save starting.
  useLayoutEffect(() => {
    if (!shouldRetry) {
      hasRetriedRef.current = false;
      return;
    }
    if (hasRetriedRef.current) return;
    hasRetriedRef.current = true;
    retry();
  }, [shouldRetry, retry]);
}
